import React, {useState, useEffect, useRef} from 'react'
import {Alert, Button, H6, IconButton, Modal, ModalHeader, ModalActions, P} from '../UI/index.js'
import {formSpacing, CurrencyInput, FormRow, FormStack, Input, MetricInput, RadioGroup, Select, Toggle} from '../UI/forms/index.js'
import type {InputRef} from '../UI/forms/Input.js'
import {fuelTypeMap, payloadTypeMap, routeProfileMap} from '../../views/planner/Vehicles.js'
import IconPicker from '../UI/forms/IconPicker.js'
import {FuelType, PayloadType, Reseller, RouteProfile} from 'types/types.js'
import dbCodes from '../../../server/dbCodes.js'
import _ from 'lodash'
import {VehicleModel} from 'server/models/vehicle.js'

interface EditVehicleTypeModalProps {
    vehicleType: Reseller['settings']['vehicleTypes'][0]
    open: boolean
    onClose: () => void
    vehicleTypes: Reseller['settings']['vehicleTypes']
    colliTypes?: {id: string, name: string}[]
    onChange: (vehicle: Reseller['settings']['vehicleTypes'][0]) => void
    onArchive: () => void
    errors?: string[]
    noArchivePossible?: boolean
}

const usageMap = {
    electric: 'kWh',
    hydrogen: 'kg'
}

export default function EditVehicleTypeModal({vehicleType, open, onClose, vehicleTypes, colliTypes = [], onChange, onArchive, errors, noArchivePossible}: EditVehicleTypeModalProps) {
    const [vehicleTypeLocal, setVehicleLocal] = useState(structuredClone(vehicleType))
    const [duplicateNameError, setDuplicateNameError] = useState('')
    const nameRef = useRef(null)
    const [iconInvalid, setIconInvalid] = useState(false)
    const routeProfileRef = useRef(null)

    const onSave = () => {
        setIconInvalid(false)
        setDuplicateNameError('')
        const requiredFields: {
            value: string
            ref?: React.RefObject<InputRef>
            action?: () => void
        }[] = [
            {
                value: vehicleTypeLocal.name,
                ref: nameRef
            }
        ]

        let fieldsAreValid = true

        requiredFields.forEach((field) => {
            if (!field.value) {
                if (field.ref) {
                    field.ref.current?.setInvalid()
                }

                if (field.action) {
                    field.action()
                }

                fieldsAreValid = false
            }
        })

        const vehicleWithSameNameExists = vehicleTypes.some((vehicleType) => vehicleType.name === vehicleTypeLocal.name && vehicleType._id !== vehicleTypeLocal._id)

        if (vehicleWithSameNameExists) {
            setDuplicateNameError('Er bestaat al een voertuigsoort met deze naam.')
            return
        }

        if (fieldsAreValid) {
            onChange(vehicleTypeLocal)
        }
    }

    useEffect(() => {
        setVehicleLocal(structuredClone(vehicleType))
    }, [vehicleType])

    useEffect(() => {
        if (!open) {
            setVehicleLocal(structuredClone(vehicleType))
            setDuplicateNameError('')
        } else {
            console.log(vehicleType)
        }
    }, [open])

    const availableColliTypes = colliTypes.length > 0 ? colliTypes : _.keys(dbCodes.colloTypes).map((key) => ({id: key, name: dbCodes.colloTypes[key]}))

    return (
        <Modal show={open} onClose={onClose} style={{maxWidth: 600}}>
            <ModalHeader title='Voertuigsoort' onClose={onClose} style={{alignItems: 'baseline'}} />

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <FormRow>
                    <Input
                        label='Naam'
                        value={vehicleTypeLocal.name}
                        onChange={(event) => {
                            setVehicleLocal({...vehicleTypeLocal, name: event.target.value})
                            setDuplicateNameError('')
                        }}
                        ref={nameRef}
                        maxLength={VehicleModel.shape.name.maxLength}
                    />

                    <IconPicker
                        label='Icoon'
                        value={vehicleTypeLocal.icon}
                        icons={['bike', 'bike-fast', 'bicycle-cargo', 'truck-trailer', 'car-side', 'van-utility', 'truck', 'car-electric', 'bus-electric', 'moped', 'moped-electric', 'walk']}
                        onChange={(event) => {
                            setVehicleLocal({...vehicleTypeLocal, icon: event.target.value})
                        }}
                        isInvalid={iconInvalid}
                    />
                </FormRow>

                {duplicateNameError && <Alert style={{marginBottom: 6}} variant='error'>{duplicateNameError}</Alert>}

                <FormRow>
                    <Select
                        label='Routeberekening'
                        value={vehicleTypeLocal.routeProfile}
                        onChange={(event) => setVehicleLocal({...vehicleTypeLocal, routeProfile: event.target.value as RouteProfile})}
                        options={
                            [
                                ...['car', 'bike', 'walk'].map((routeProfile) => ({
                                    value: routeProfile,
                                    title: routeProfileMap[routeProfile]
                                }))
                            ]
                        }
                        ref={routeProfileRef}
                    />

                    <Toggle
                        label='Snelheid aanpassen'
                        checked={vehicleTypeLocal.speedDifferenceEnabled}
                        onChange={(event) => setVehicleLocal({...vehicleTypeLocal, speedDifferenceEnabled: event.target.checked})}
                    />
                </FormRow>

                {vehicleTypeLocal.speedDifferenceEnabled &&
                    <FormRow>
                        <div style={{flex: 1}}>
                            <RadioGroup
                                options={[
                                    {value: 'faster', label: 'Sneller'},
                                    {value: 'slower', label: 'Langzamer'}
                                ]}
                                value={vehicleTypeLocal.speedDifferenceMode || 'faster'}
                                onChange={(event) => setVehicleLocal({...vehicleTypeLocal, speedDifferenceMode: event.target.value as 'faster' || 'slower'})}
                            />
                        </div>

                        <Input
                            type='number'
                            label='Snelheidsafwijking'
                            value={vehicleTypeLocal.speedDifference}
                            min={0}
                            max={vehicleTypeLocal.speedDifferenceMode === 'faster' ? 99 : undefined}
                            onChange={(event) => setVehicleLocal({...vehicleTypeLocal, speedDifference: parseInt(event.target.value) || 0})}
                            append='%'
                        />
                    </FormRow>
                }

                <MetricInput
                    label='Stoptijd'
                    value={vehicleTypeLocal.stopTime}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, stopTime: event.target.value})}
                    append='s'
                />

                <Select
                    label='Aandrijving / brandstof'
                    value={vehicleTypeLocal.fuelType}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, fuelType: event.target.value as FuelType})}
                    options={
                        [
                            ...['green-electric', 'grey-electric', 'unknown-electric', 'bio-diesel', 'diesel', 'lpg', 'hydrogen', 'none'].map((fuelType) => ({
                                value: fuelType,
                                title: fuelTypeMap[fuelType]
                            }))
                        ]
                    }
                />

                {vehicleTypeLocal.fuelType !== 'none' &&
                    <MetricInput
                        label={`Verbruik (${usageMap[Object.keys(usageMap).find((key) => vehicleTypeLocal.fuelType.includes(key))] || 'L'})`}
                        value={vehicleTypeLocal.usage}
                        onChange={(event) => setVehicleLocal({...vehicleTypeLocal, usage: event.target.value})}
                        divisionFactor={1}
                        precision={2}
                        append='per 100km'
                    />
                }

                <MetricInput
                    label='Bereik'
                    value={vehicleTypeLocal.range}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, range: event.target.value})}
                    divisionFactor={1000}
                    append='km'
                />
            </FormStack>

            <H6 style={{marginBottom: 12, marginTop: 36}}>Laadvermogen</H6>
            <FormStack style={{marginBottom: formSpacing.section}}>
                {vehicleTypeLocal.payloads?.map((payload, index) => (
                    <FormRow key={`payloadRow-${index}`} style={{alignItems: 'baseline'}}>
                        <FormRow style={{width: '50%', alignItems: 'center'}}>
                            {vehicleTypeLocal.payloads.length > 1 && <P>Of</P>}
                            <Select
                                label='Laadvermogen type'
                                value={payload.payloadType}
                                onChange={(event) => {
                                    const payloads = vehicleTypeLocal.payloads

                                    payloads[index] = {...payloads[index], payloadType: event.target.value as PayloadType}

                                    setVehicleLocal({...vehicleTypeLocal, payloads})
                                }
                                }
                            >
                                <option value='weight'>{payloadTypeMap['weight']}</option>
                                <option value='colli'>{payloadTypeMap['colli']}</option>
                            </Select>

                            {payload.payloadType === 'colli' &&
                                <Select
                                    label='Type collo'
                                    value={payload.payloadColliType}
                                    onChange={(event) => {
                                        const payloads = vehicleTypeLocal.payloads

                                        payloads[index] = {...payloads[index], payloadColliType: event.target.value}

                                        setVehicleLocal({...vehicleTypeLocal, payloads})
                                    }}
                                    allowEmpty
                                    options={availableColliTypes.map((type) => {
                                        return {value: type.id, title: type.name}
                                    })}
                                />
                            }
                        </FormRow>

                        <MetricInput
                            label='Laadvermogen'
                            value={payload.payload}
                            onChange={(event) => {
                                const payloads = vehicleTypeLocal.payloads

                                payloads[index] = {...payloads[index], payload: event.target.value}

                                setVehicleLocal({...vehicleTypeLocal, payloads})
                            }}
                            divisionFactor={payload.payloadType === 'weight' ? 1000 : 1}
                            append={payload.payloadType === 'weight' ? 'kg' : 'stuks'}
                        />

                        <IconButton
                            icon='mdi mdi-delete'
                            onClick={() => {
                                const payloads = vehicleTypeLocal.payloads

                                payloads.splice(index, 1)

                                setVehicleLocal({...vehicleTypeLocal, payloads})
                            }}
                        />
                    </FormRow>
                ))}

                <Button
                    variant='outline-white'
                    label='Laadvermogen toevoegen'
                    icon='mdi mdi-plus'
                    onClick={() => {
                        const payloads = vehicleTypeLocal.payloads || []

                        payloads.push({payloadType: '', payload: 0})

                        setVehicleLocal({...vehicleTypeLocal, payloads})
                    }}
                />
            </FormStack>

            <H6 style={{marginBottom: 12, marginTop: 36}}>Overig</H6>
            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <CurrencyInput
                    label='Uurtarief voertuig'
                    value={vehicleTypeLocal.hourlyRate}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, hourlyRate: event.target.value})}
                />

                <Input
                    label='Opmerking voor koerier'
                    value={vehicleTypeLocal.comment}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, comment: event.target.value})}
                />

                <Input
                    label='Notities'
                    value={vehicleTypeLocal.notes}
                    onChange={(event) => setVehicleLocal({...vehicleTypeLocal, notes: event.target.value})}
                />
            </FormStack>

            {errors?.length > 0 && errors.map((error, index) => (
                <Alert style={{marginBottom: 6}} key={index} variant='error'>
                    {error}
                </Alert>
            ))}

            <ModalActions>
                {vehicleTypeLocal._id && (vehicleTypeLocal.isArchived || !noArchivePossible) ?
                    <Button onClick={onArchive}>
                        {vehicleTypeLocal.isArchived ? 'Dearchiveren' : 'Archiveren'}
                    </Button> :
                    <Button onClick={onClose}>Annuleren</Button>
                }

                <Button
                    disabled={!vehicleTypeLocal.name}
                    onClick={onSave}
                >
                    Opslaan
                </Button>
            </ModalActions>
        </Modal>
    )
}
